import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Pages
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminQuestionReviewPage from "./pages/AdminQuestionReviewPage";
import AdminBugReportsPage from "./pages/AdminBugReportsPage";
import AuthPage from './pages/AuthPage';
import CreateUsernamePage from './pages/CreateUsernamePage';
import DashboardPage from './pages/DashboardPage';
import FlaggedReportsPage from './pages/FlaggedReportsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ProfilePage from './pages/ProfilePage';
import QuestionAddPage from './pages/QuestionAddPage';
import QuestionEditPage from './pages/QuestionEditPage';
import QuizPage from './pages/QuizPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ResultsPage from './pages/ResultsPage';
import ReviewPage from './pages/ReviewPage';
import UserManagementPage from './pages/UserManagementPage';
import AdminFeedbackPage from './pages/AdminFeedbackPage';
import ThemeCustomizer from './components/ThemeCustomizer';
import ProfileSettings from './pages/ProfileSettings';
import BillingPage from './pages/BillingPage';
import PurchaseStatesPage from './pages/PurchaseStatesPage';
import AdminQuestionManager from './pages/AdminQuestionManager';

// Components
import MainLayout from './layouts/MainLayout';
// import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';

// Route Guards
import { 
  PrivateRoute, 
  PermissionRoute, 
  SubscriptionRoute,
 //  GuardedRoute 
} from './components/RouteGuards';

// Contexts
import { ToastProvider } from './components/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './AuthContext';

import './main.css';
import './css/MobileResponsive.css';

// Loading component for route transitions
const RouteLoading = () => (
  <div className="route-loading-container">
    <LoadingSpinner />
    <p>Loading...</p>
  </div>
);

// Public route wrapper
const PublicRoute = ({ children, allowWhenAuthenticated = false }) => {
  const { isLoggedIn, isLoading } = useAuth();
  
  // Debug logging
  console.log('[PublicRoute Debug]', { 
    isLoggedIn, 
    isLoading, 
    allowWhenAuthenticated,
    path: window.location.pathname,
    search: window.location.search
  });
  
  if (isLoading) return <RouteLoading />;
  
  // Allow access to certain routes even when authenticated (like password reset)
  if (isLoggedIn && !allowWhenAuthenticated) {
    console.log('[PublicRoute] Redirecting to dashboard - user is logged in');
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// App routes component
function AppRoutes() {
  const { isLoading } = useAuth();

  if (isLoading) return <RouteLoading />;

  return (
    <Routes>
      {/* PUBLIC ROUTES */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute allowWhenAuthenticated={true}>
            <ResetPasswordPage />
          </PublicRoute>
        } 
      />

      {/* AUTHENTICATED ROUTES */}
      <Route
        element={
          <PrivateRoute>
            <MainLayout />
          </PrivateRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/settings" element={<ProfileSettings />} />
        <Route path="/profile/theme" element={<ThemeCustomizer />} />
        <Route path="/create-username" element={<CreateUsernamePage />} />

        {/* QUIZ ROUTES */}
        <Route 
          path="/quiz" 
          element={
            <SubscriptionRoute allowTrial={true}>
              <QuizPage />
            </SubscriptionRoute>
          } 
        />
        <Route 
          path="/results" 
          element={
            <SubscriptionRoute allowTrial={true}>
              <ResultsPage />
            </SubscriptionRoute>
          } 
        />
        <Route 
          path="/review" 
          element={
            <SubscriptionRoute>
              <ReviewPage />
            </SubscriptionRoute>
          } 
        />

        {/* BILLING */}
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/billing/upgrade" element={<BillingPage upgradeMode={true} />} />
        <Route path="/states/purchase" element={<PurchaseStatesPage />} />

        {/* ADMIN ROUTES */}
        <Route 
          path="/admin/dashboard" 
          element={
            <PermissionRoute requiredPermission="view_analytics">
              <AdminDashboardPage />
            </PermissionRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <PermissionRoute requiredPermission="manage_users">
              <UserManagementPage />
            </PermissionRoute>
          } 
        />
        <Route 
          path="/admin/questions" 
          element={
            <PermissionRoute requiredPermission="approve_questions">
              <AdminQuestionManager />
            </PermissionRoute>
          } 
        />
        <Route 
          path="/admin/questions/add" 
          element={
            <PermissionRoute requiredPermission="bulk_upload_questions">
              <QuestionAddPage />
            </PermissionRoute>
          } 
        />
        <Route 
          path="/admin/questions/:id/edit" 
          element={
            <PermissionRoute requiredPermission="approve_questions">
              <QuestionEditPage />
            </PermissionRoute>
          } 
        />
        <Route 
          path="/admin/questions/pending" 
          element={
            <PermissionRoute requiredPermission="approve_questions">
              <AdminQuestionReviewPage />
            </PermissionRoute>
          } 
        />
        <Route 
          path="/admin/reports" 
          element={
            <PermissionRoute requiredPermission="review_feedback">
              <FlaggedReportsPage />
            </PermissionRoute>
          } 
        />
        <Route 
          path="/admin/feedback" 
          element={
            <PermissionRoute requiredPermission="review_feedback">
              <AdminFeedbackPage />
            </PermissionRoute>
          } 
        />
        <Route 
          path="/admin/bug-reports" 
          element={
            <PermissionRoute requiredPermission="review_feedback">
              <AdminBugReportsPage />
            </PermissionRoute>
          } 
        />
      </Route>

      {/* FALLBACK ROUTES */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/404" element={
        <div className="error-page">
          <h1>404 - Page Not Found</h1>
          <p>The page you're looking for doesn't exist.</p>
          <a href="/dashboard">Go to Dashboard</a>
        </div>
      } />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}

// Error Boundary for the entire app
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error Boundary caught an error:', error, errorInfo);
    
    if (window.logError) {
      window.logError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="app-error-boundary">
          <div className="error-container">
            <h1>Something went wrong</h1>
            <p>We're sorry, but something unexpected happened.</p>
            <button 
              onClick={() => window.location.reload()}
              className="error-reload-btn"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main App component
function App() {
  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}
            >
              <AppRoutes />
            </BrowserRouter>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}

export default App;